import Component from '../core/Component'
import ScrollService from '../services/Scroll'
import { substitute } from '../utils/String'
import fuzzysort from 'fuzzysort'

export default class FormSelectDropdown extends Component {
	constructor(element, options) {
		super(
			element,
			(options = {
				anchorEl: null,
				isSearchVisible: false,
				initialChoices: [],
				initialSelectedValues: [],
				choiceTemplate: `<span>{nameWithMarks}</span>`,
				...options,
			})
		)

		this.ref = {
			list: null,
			search: null,
			message: null,
		}

		this.anchorEl = this.options.anchorEl
		this.choices = this.options.initialChoices.map((choice) => this.normalizeChoice(choice))
		this.selectedValues = this.options.initialSelectedValues
		this.highlightedValue = null
	}

	prepare() {
		this.element.classList.toggle('is-search-visible', !!this.options.isSearchVisible)
		this.renderList()
		this.renderMessage()
		this.updatePosition()
		this.ref.list.addEventListener('click', this.handleListClick)
		this.ref.search.addEventListener('keyup', this.handleSearchKeyUp)
		document.body.addEventListener('click', this.handleOuterClick)
		document.body.addEventListener('keydown', this.handleOuterKeyDown)
		this.ref.search.focus()
	}

	destroy() {
		document.body.removeEventListener('click', this.handleOuterClick)
		document.body.removeEventListener('keydown', this.handleOuterKeyDown)
		this.emit('close')
	}

	handleOuterKeyDown = (event) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault()
			this.highlightNext()
		} else if (event.key === 'ArrowUp') {
			event.preventDefault()
			this.highlightPrev()
		} else if (event.key === 'Enter') {
			event.preventDefault()
			this.select(this.highlightedValue)
		} else if (event.key === 'Escape') {
			this.close()
		} else {
			//...
		}
	}

	handleSearchKeyUp = async () => {
		this.renderMessage()
		this.renderList()
	}

	handleOuterClick = (event) => {
		if (this.anchorEl.contains(event.target) || this.element.contains(event.target) || event.target.closest('[data-value]')) {
			return
		}

		this.close()
	}

	handleListClick = (event) => {
		const el = event.target.closest('[data-value]')
		if (el) {
			this.select(el.dataset.value)
		}
	}

	updatePosition() {
		if (!this.anchorEl) {
			return
		}

		const anchorRect = this.anchorEl.getBoundingClientRect()
		const scroll = ScrollService.isVirtual ? {x:0,y:0} : ScrollService.getScroll()

		this.element.style.position = `absolute`
		this.element.style.top = `${scroll.y + anchorRect.top + anchorRect.height}px`
		this.element.style.left = `${scroll.x + anchorRect.left}px`
		this.element.style.minWidth = `${anchorRect.width}px`
	}

	renderList() {
		const choices = this.getVisibleChoices(this.choices)
		this.ref.list.innerHTML = choices
			.map(
				(choice) => `
						<div class="FormSelectDropdown-item ${this.selectedValues.includes(choice.value) ? 'is-selected' : ''} ${
					this.highlightedValue === choice.value ? 'is-highlighted' : ''
				}" data-value="${choice.value}">
							${substitute(this.options.choiceTemplate, choice)}
						</div>    
			`
			)
			.join('')
	}

	renderMessage() {
		this.ref.message.innerHTML = `
            ${this.ref.search.value && !this.getVisibleChoices(this.choices).length ? '<p>Nic nenalezeno.</p>' : ''}
            ${!this.ref.search.value && !this.getVisibleChoices(this.choices).length ? '<p>Žádné možnosti na výběr.</p>' : ''}
        `
	}

	getRemainingChoices(choices) {
		return choices.filter((choice) => !this.selectedValues.includes(choice.value))
	}

	getSearchedChoices(choices) {
		let value = this.ref.search.value
		value = value.normalize('NFD').replace(/\p{Diacritic}/gu, '')

		if (value) {
			const results = fuzzysort.go(value, choices, {
				key: 'normValue',
			})
			return results.map((result) => {
				result.target = result.obj.name
				return {
					...result.obj,
					nameWithMarks: result.highlight(),
				}
			})
		} else {
			return choices.map((choice) => ({
				...choice,
				nameWithMarks: choice.name,
			}))
		}
	}

	getVisibleChoices(choices) {
		return this.getSearchedChoices(this.getRemainingChoices(choices))
	}

	setSelectedValues(selectedValues) {
		this.selectedValues = selectedValues
		this.renderList()
		this.renderMessage()
	}

	normalizeChoice(choice) {
		return {
			...choice,
			normValue: choice.name.normalize('NFD').replace(/\p{Diacritic}/gu, ''),
		}
	}

	close() {
		this.destroy()
		this.element.parentNode.removeChild(this.element)
	}

	highlightNext() {
		const choices = this.getVisibleChoices(this.choices)
		if (!choices.length) {
			return
		}
		const index = choices.findIndex((choice) => choice.value === this.highlightedValue) ?? -1
		const nextIndex = Math.min(index + 1, choices.length - 1)
		this.highlightedValue = choices[nextIndex].value
		this.renderList()
	}

	highlightPrev() {
		const choices = this.getVisibleChoices(this.choices)
		if (!choices.length) {
			return
		}
		const index = choices.findIndex((choice) => choice.value === this.highlightedValue)
		const prevIndex = Math.max(index - 1, 0)
		this.highlightedValue = choices[prevIndex].value
		this.renderList()
	}

	select(value) {
		const choice = this.choices.find((choice) => choice.value === value)
		if (choice) {
			this.emit('select', { choice })
		}
	}
}

export function createFormSelectDropdown(options) {
	const bufferEl = document.createElement('div')
	bufferEl.innerHTML = `
        <div class="FormSelectDropdown">
            <input class="FormSelectDropdown-search" type="text" data-ref="search"/>
            <div class="FormSelectDropdown-message" data-ref="message"></div>
            <div class="FormSelectDropdown-list" data-ref="list">
                
            </div>
        </div>
    `

	const rootEl = bufferEl.firstElementChild
	document.body.appendChild(rootEl)

	const component = new FormSelectDropdown(rootEl, options)
	component.prepare()
	return component
}
